/** Admin City Reviews — API mappers & listing config. */

import dayjs from "dayjs"
import { getUserFullName } from "../../../helpers/userDisplay"

export const CITY_REVIEW_STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Under Review" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
]

export const REVIEW_SORT_OPTIONS = [
  { value: "created_at", label: "Created At" },
  { value: "published_at", label: "Published At" },
]

export const DEFAULT_REVIEW_SORT_BY = "created_at"
export const DEFAULT_REVIEW_SORT_DIRECTION = "desc"

export const REVIEW_MAX_MEDIAS = 3
export const REVIEW_MAX_MEDIA_BYTES = 5 * 1024 * 1024
export const REVIEW_MEDIA_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp"

/** UI status ← API status */
export const mapApiStatusToUi = status => {
  const value = String(status || "")
    .trim()
    .toLowerCase()
  if (value === "published" || value === "approved") return "approved"
  if (value === "rejected") return "rejected"
  if (value === "pending" || value === "under_review") return "pending"
  return value || "pending"
}

/** API status filter ← UI tab/filter */
export const mapUiStatusToApi = status => {
  if (status === "approved") return "published"
  return status
}

const getReviewerName = user => {
  if (!user) return "--"
  return getUserFullName(user, { fallback: "--" })
}

const formatDate = value => {
  if (!value) return "--"
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : "--"
}

/**
 * Map GET /api/v1/admin/reviews item → table row.
 */
export const mapReviewRow = item => {
  const user = item.user || {}
  const uiStatus = mapApiStatusToUi(item.status)

  return {
    id: item.id,
    reviewer: getReviewerName(user),
    reviewerImage:
      user.profile_media?.thumbnail ||
      user.profile_media?.image ||
      user.thumbnail ||
      user.image ||
      "",
    title: item.title || "--",
    description: item.description || "",
    date: formatDate(item.created_at || item.published_at),
    createdAt: item.created_at || "",
    publishedAt: item.published_at || "",
    status: uiStatus,
    apiStatus: item.status || "",
    cityId: item.city?.id || "",
    cityLabel: item.city
      ? [item.city.city, item.city.state].filter(Boolean).join(", ")
      : "--",
    medias: Array.isArray(item.medias) ? item.medias : [],
    raw: item,
  }
}

/**
 * Map GET /api/v1/admin/reviews/:id → detail/edit model.
 */
export const mapReviewDetail = item => {
  const row = mapReviewRow(item || {})
  return {
    ...row,
    actionBy: item?.action_by || null,
    actionReason: item?.action_reason || "",
    rejectedAt: item?.rejected_at || "",
  }
}

const appendArray = (formData, key, values = []) => {
  ;(Array.isArray(values) ? values : []).forEach(value => {
    if (value == null || value === "") return
    formData.append(`${key}[]`, value)
  })
}

/**
 * Build multipart FormData for create/update admin review.
 * Update uses POST + `_method=PATCH` so Laravel parses medias[].
 */
export const buildReviewFormData = ({
  values,
  cityId,
  isEdit = false,
} = {}) => {
  const formData = new FormData()
  if (isEdit) formData.append("_method", "PATCH")

  formData.append("title", String(values?.title || "").trim())
  formData.append("description", String(values?.description || "").trim())

  if (!isEdit) {
    const resolvedCityId = values?.cityId || cityId
    if (resolvedCityId) formData.append("city_id", resolvedCityId)
  }

  const previews = Array.isArray(values?.previews) ? values.previews : []
  const newFiles = previews
    .map(item => item?.file)
    .filter(file => file instanceof File)
    .slice(0, REVIEW_MAX_MEDIAS)

  newFiles.forEach(file => {
    formData.append("medias[]", file)
  })

  if (isEdit) {
    const keptExistingIds = new Set(
      previews
        .filter(item => item?.isExisting && item?.mediaId)
        .map(item => String(item.mediaId)),
    )
    const initialIds = (
      Array.isArray(values?.initialMediaIds) ? values.initialMediaIds : []
    )
      .map(id => String(id || "").trim())
      .filter(Boolean)
    const explicitRemoved = (
      Array.isArray(values?.removedMediaIds) ? values.removedMediaIds : []
    )
      .map(id => String(id || "").trim())
      .filter(Boolean)

    const removedIds = [
      ...explicitRemoved,
      ...initialIds.filter(id => !keptExistingIds.has(id)),
    ]

    appendArray(formData, "remove_media_ids", [...new Set(removedIds)])
  }

  return formData
}
